// @refresh reset — dev-only: force remount so the useMemo([]) board nodes hot-reload on edit (no prod effect)
import { useMemo } from "react";
import { type Node, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";

// ─────────────────────────────────────────────────────────────────────────────
// OPEN SOURCE BOARD — the R&D venture: a beautiful, curated, SEO-first directory
// of open-source projects for people getting into Claude Code, built to pull
// organic + AI-cited search traffic and monetize it.
//
// This board is the single pane of glass for the venture. It brings ALL the
// research home from the Claude Code artifacts as native, document-quality
// page-cards (no external artifacts): the market brief (opportunity, competition,
// demand, money) and the SEO/AEO-GEO playbook (why we win, the two engines, the
// roadmap) — plus the business-plan pitch deck.
//
// Sections are SectionBoxes packed into one SectionGrid (Erez-locked board law):
// each box = a gray container, one big black pill title, white document cards
// inside. Owned by Atlas.
// ─────────────────────────────────────────────────────────────────────────────

const T = {
  bg: "#E5E5E5", dots: "#CFCFCF",
  ink: "#16201C", ink2: "#4A554F", faint: "#8A948F",
  line: "#E3E6E2", card: "#FFFFFF", soft: "#FAFBFA",
  accent: "#0E7C66", accentWash: "#E9F3EF",
  good: "#2E7D5B", warn: "#B7791F", crit: "#C0492F",
};
const FONT = "Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const PAGE_W = 1180;

// ── tiny inline-rich renderer: **bold** and `code` ───────────────────────────
function rich(text: string) {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((seg, i) => {
    if (seg.startsWith("**") && seg.endsWith("**"))
      return <b key={i} style={{ color: T.ink, fontWeight: 700 }}>{seg.slice(2, -2)}</b>;
    if (seg.startsWith("`") && seg.endsWith("`"))
      return <code key={i} style={{ fontFamily: MONO, fontSize: "0.86em", background: T.accentWash, color: T.accent, padding: "1px 6px", borderRadius: 5 }}>{seg.slice(1, -1)}</code>;
    return <span key={i}>{seg}</span>;
  });
}

// ── shared atoms ─────────────────────────────────────────────────────────────
function Eyebrow({ children, color = T.accent }: { children: React.ReactNode; color?: string }) {
  return <div style={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color }}>{children}</div>;
}
function Chip({ children, fg, bg }: { children: React.ReactNode; fg: string; bg: string }) {
  return <span style={{ display: "inline-block", fontFamily: MONO, fontSize: 15, fontWeight: 700, letterSpacing: "0.02em", color: fg, background: bg, borderRadius: 7, padding: "5px 12px", whiteSpace: "nowrap" }}>{children}</span>;
}
function PageTitle({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 46, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.1, color: T.ink }}>{children}</div>;
}
function Lede({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 24, lineHeight: 1.5, color: T.ink2, maxWidth: 62 * 15 }}>{children}</div>;
}
function P({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 20, lineHeight: 1.6, color: T.ink2 }}>{children}</div>;
}
function Bullets({ items, dot = T.accent }: { items: string[]; dot?: string }) {
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map((it, i) => (
        <li key={i} style={{ position: "relative", paddingLeft: 26, fontSize: 20, lineHeight: 1.55, color: T.ink2 }}>
          <span style={{ position: "absolute", left: 4, top: 12, width: 8, height: 8, borderRadius: "50%", background: dot }} />
          {rich(it)}
        </li>
      ))}
    </ul>
  );
}

// white document page card
function Page({ children, w = PAGE_W, accent }: { children: React.ReactNode; w?: number; accent?: string }) {
  return (
    <div style={{
      width: w, boxSizing: "border-box", background: T.card,
      border: `1px solid ${T.line}`, borderTop: accent ? `5px solid ${accent}` : undefined,
      borderRadius: 24, boxShadow: "0 12px 40px rgba(22,32,28,0.07)",
      padding: 56, fontFamily: FONT, display: "flex", flexDirection: "column", gap: 26,
    }}>
      {children}
    </div>
  );
}

// KPI strip
function KpiStrip({ items }: { items: { v: string; l: string; accent?: boolean }[] }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 1, background: T.line, border: `1px solid ${T.line}`, borderRadius: 16, overflow: "hidden" }}>
      {items.map((k, i) => (
        <div key={i} style={{ background: T.soft, padding: "22px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontFamily: MONO, fontSize: 30, fontWeight: 700, letterSpacing: "-0.02em", color: k.accent ? T.accent : T.ink, fontVariantNumeric: "tabular-nums" }}>{k.v}</div>
          <div style={{ fontSize: 15, lineHeight: 1.35, color: T.ink2 }}>{rich(k.l)}</div>
        </div>
      ))}
    </div>
  );
}

// generic table
type Cell = { text: string; sub?: string; mono?: boolean; tag?: "no" | "yes" | "soft"; strong?: string };
function Table({ headers, rows, hiFirst }: { headers: string[]; rows: Cell[][]; hiFirst?: boolean }) {
  const tagStyle = (t: Cell["tag"]) =>
    t === "no" ? { color: T.crit, bg: "rgba(192,73,47,0.12)" } :
    t === "yes" ? { color: T.good, bg: "rgba(46,125,91,0.14)" } :
    { color: T.warn, bg: "rgba(183,121,31,0.14)" };
  return (
    <div style={{ border: `1px solid ${T.line}`, borderRadius: 16, overflow: "hidden" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 18 }}>
        <thead>
          <tr>{headers.map((h, i) => (
            <th key={i} style={{ fontFamily: MONO, fontSize: 14, letterSpacing: "0.06em", textTransform: "uppercase", color: T.faint, fontWeight: 700, textAlign: "left", padding: "16px 20px", borderBottom: `1px solid ${T.line}`, background: T.soft, whiteSpace: "nowrap" }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} style={{ background: hiFirst && ri === 0 ? T.accentWash : T.card }}>
              {r.map((c, ci) => (
                <td key={ci} style={{ padding: "17px 20px", borderBottom: ri === rows.length - 1 ? "none" : `1px solid ${T.line}`, verticalAlign: "top", color: T.ink2, lineHeight: 1.45 }}>
                  {c.tag
                    ? <span style={{ display: "inline-block", fontFamily: MONO, fontSize: 15, fontWeight: 700, padding: "3px 10px", borderRadius: 6, ...(() => { const s = tagStyle(c.tag); return { color: s.color, background: s.bg }; })() }}>{c.text}</span>
                    : <>
                        <span style={{ display: "block", fontWeight: c.sub || ci === 0 ? 700 : 500, color: c.strong ? c.strong : (ci === 0 ? T.ink : T.ink2), fontFamily: c.mono ? MONO : FONT, fontVariantNumeric: c.mono ? "tabular-nums" : undefined }}>{rich(c.text)}</span>
                        {c.sub && <span style={{ display: "block", fontSize: 14, color: T.faint, fontFamily: MONO, marginTop: 3 }}>{c.sub}</span>}
                      </>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ eyebrow, children, tone = "dashed" }: { eyebrow: string; children: React.ReactNode; tone?: "dashed" | "solid" | "warn" }) {
  const solid = tone === "solid";
  return (
    <div style={{
      border: solid ? `1px solid ${T.accent}` : `1px dashed ${tone === "warn" ? "#D9C08A" : "#CDD3CE"}`,
      background: solid ? T.accentWash : T.soft, borderRadius: 16, padding: "24px 26px",
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <Eyebrow color={tone === "warn" ? T.warn : T.accent}>{eyebrow}</Eyebrow>
      <div style={{ fontSize: 19, lineHeight: 1.55, color: solid ? T.ink : T.ink2 }}>{children}</div>
    </div>
  );
}

// mini stat card (grid of them)
function StatGrid({ cards, cols = 3 }: { cards: { lbl: string; h: string; body: string; src?: string; big?: string }[]; cols?: number }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 18 }}>
      {cards.map((c, i) => (
        <div key={i} style={{ background: T.soft, border: `1px solid ${T.line}`, borderRadius: 16, padding: 26, display: "flex", flexDirection: "column", gap: 10 }}>
          <Eyebrow>{c.lbl}</Eyebrow>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.ink, letterSpacing: "-0.01em" }}>{c.h}</div>
            {c.big && <div style={{ fontFamily: MONO, fontSize: 30, fontWeight: 700, color: T.accent, letterSpacing: "-0.02em" }}>{c.big}</div>}
          </div>
          <div style={{ fontSize: 17, lineHeight: 1.5, color: T.ink2 }}>{rich(c.body)}</div>
          {c.src && <div style={{ fontFamily: MONO, fontSize: 13, color: T.faint }}>{c.src}</div>}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 1 — THE OPPORTUNITY  (market brief hero + verdict + KPIs)
// ═══════════════════════════════════════════════════════════════════════════
function OpportunitySection() {
  return (
    <SectionBox title="The Opportunity" layout="custom">
      <Page w={1320} accent={T.accent}>
        <Eyebrow>The concept · Atlas R&amp;D market brief · 2026-07-20</Eyebrow>
        <PageTitle>A beautiful, curated directory of open-source projects for people getting into Claude Code.</PageTitle>
        <Lede>Built to pull organic + AI-cited search traffic and monetize it. Here's who owns the lane, how big the audience is, and what a site like this actually makes — hype stripped out in verification.</Lede>

        <div style={{ background: T.card, border: `1px solid ${T.line}`, borderLeft: `4px solid ${T.accent}`, borderRadius: 16, padding: "26px 28px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <Chip fg="#FFFFFF" bg={T.accent}>GO — PICK THE WEDGE</Chip>
            <span style={{ fontSize: 22, fontWeight: 700, color: T.ink, letterSpacing: "-0.01em" }}>Real gap, real demand. The one unknown is answered.</span>
          </div>
          <P>The niche has direct competitors — but <b style={{ color: T.ink }}>none monetize</b>, and the dominant one is an ugly GitHub markdown list. And the demand question is settled: <b style={{ color: T.ink }}>"claude code" is &gt;100K searches/mo (US) and rising</b>. The head terms are hard to rank, but the <b style={{ color: T.ink }}>commercial "best [X] tools / servers" queries sit at Medium difficulty and &gt;1,000/mo</b> — exactly what a directory wins. Demand is no longer the risk; execution and positioning are.</P>
        </div>

        <KpiStrip items={[
          { v: "50.4k★", l: "The incumbent to beat: a free GitHub list (`awesome-claude-code`)" },
          { v: "$0", l: "Monetization by **any** niche web directory today", accent: true },
          { v: "$80k/yr", l: "Best comparable: OpenAlternative, solo-built" },
          { v: "73%", l: "Of niche sites get <1k visits/mo in year one" },
          { v: "84%", l: "Of developers use or plan to use AI coding tools" },
        ]} />
        <div style={{ fontFamily: MONO, fontSize: 14, color: T.faint, lineHeight: 1.7 }}>
          Deep-research harness · 6 angles · 25 sources fetched · 110 claims → 17 confirmed / 8 refuted · adversarially verified 3-vote.
        </div>
      </Page>
    </SectionBox>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 2 — THE COMPETITION
// ═══════════════════════════════════════════════════════════════════════════
function CompetitionSection() {
  return (
    <SectionBox title="The Competition" layout="custom">
      <Page w={1320}>
        <Eyebrow>01 · Competitive landscape</Eyebrow>
        <PageTitle>Lightly contested at the niche — wide open on monetization.</PageTitle>
        <P>Three players sit in the exact "Claude Code resources" niche. All free, none run ads, sponsorships, or paid listings. Above them: a mature category of general AI-tool directories — those make money, and their playbook is copyable.</P>
        <Table
          headers={["Player", "What it is", "Scale", "Monetized?", "Read on it"]}
          hiFirst
          rows={[
            [{ text: "awesome-claude-code", sub: "hesreallyhim · GitHub" }, { text: "The flagship hand-curated list. Automated CSV→README, formal PR pipeline." }, { text: "50.4k★ / 4.4k⑂", mono: true }, { text: "✕ none", tag: "no" }, { text: "Owns the head SEO term. Ugly markdown, zero design or onboarding." }],
            [{ text: "AwesomeClaude.ai", sub: "by Webfuse (agency)" }, { text: "Live directory: Claude Code, MCP, skills, prompts, agents — 7 sections." }, { text: "7-section taxonomy", mono: true }, { text: "~ agency lead-gen", tag: "soft" }, { text: "Closest direct rival. Polished-ish, no revenue engine." }],
            [{ text: "Claude Code Navigator", sub: "claude-hub.com" }, { text: "Directory of resources, per-item pages, Google-Form submissions." }, { text: "264 resources", mono: true }, { text: "✕ none", tag: "no" }, { text: "~168 entries auto-scraped from GitHub. Thin." }],
            [{ text: "There's An AI For That", sub: "TAAFT · generalist" }, { text: "Global #1 AI-tools directory. The scale ceiling & the money playbook." }, { text: "~4.6M/mo · 41k+ tools", mono: true }, { text: "✓ paid + sponsor", tag: "yes" }, { text: "Proof the model prints money at scale. ~52% organic." }],
            [{ text: "Toolify", sub: "generalist" }, { text: "Mature AI-tools directory. Clear, documented rate card to copy." }, { text: "~1.4–3M/mo", mono: true }, { text: "✓ paid + sponsor", tag: "yes" }, { text: "~$100 skip-the-queue; sponsored $100s–$1,000s/mo. ~47% organic." }],
            [{ text: "OpenAlternative", sub: "the real analog" }, { text: "Solo-built directory of open-source alternatives. Built in a weekend." }, { text: "~70k visits/mo", mono: true }, { text: "✓ $80k/yr", tag: "yes" }, { text: "The proof-of-concept for THIS business — broader keyword." }],
          ]}
        />
        <Callout eyebrow="The gap, stated plainly">
          A <b style={{ color: T.ink }}>polished, SEO-first, monetized</b> Claude-Code directory does not exist. The niche players don't monetize; the monetizers aren't niche. That intersection is the whole opportunity — and the whole risk, because "nobody monetizes it yet" can mean "no money in it yet."
        </Callout>
      </Page>
    </SectionBox>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 3 — THE DEMAND
// ═══════════════════════════════════════════════════════════════════════════
function DemandSection() {
  return (
    <SectionBox title="The Demand" layout="custom">
      <Page w={1320} accent={T.accent}>
        <Eyebrow>02 · Keyword demand · the gate</Eyebrow>
        <PageTitle>The one unknown, answered with live data: demand is real and large.</PageTitle>
        <P>Live monthly search volumes (Ahrefs clickstream, US). The pattern is decisive: <b style={{ color: T.ink }}>head terms are huge but hard to rank</b>; the <b style={{ color: T.ink }}>commercial "best [X] tools/servers" queries are Medium difficulty at &gt;1,000/mo</b> — the directory's winnable engine.</P>
        <Table
          headers={["Seed topic", "Head vol. (US/mo)", "Variations", "Head KD", "Winnable directory query"]}
          hiFirst
          rows={[
            [{ text: "claude code", sub: "the beachhead brand" }, { text: ">100K", mono: true, strong: T.accent }, { text: "107,425", mono: true }, { text: "Hard", tag: "no" }, { text: "`claude code mcp servers / agents` — >100–1k" }],
            [{ text: "vibe coding", sub: "the zeitgeist term" }, { text: ">10,000", mono: true }, { text: "10,380", mono: true }, { text: "Hard", tag: "no" }, { text: "**best vibe coding tools** — >1k · Medium ✓" }],
            [{ text: "mcp servers", sub: "the catalog core" }, { text: ">1,000", mono: true }, { text: "4,987", mono: true }, { text: "Hard", tag: "no" }, { text: "**best / claude mcp servers** — >1k · Medium ✓" }],
            [{ text: "ai coding tools", sub: "the broad lane" }, { text: ">1,000", mono: true }, { text: "2,121", mono: true }, { text: "Hard", tag: "no" }, { text: "**best ai coding tools** — >1k · Medium ✓" }],
          ]}
        />
        <Callout eyebrow="What this settles" tone="solid">
          <b style={{ color: T.ink }}>Demand is no longer the risk.</b> "Claude Code" alone draws &gt;100K US searches/mo, freshly and actively searched. You won't out-rank Anthropic on the head term — but you don't need to. The repeatable winnable pattern is the <b style={{ color: T.ink }}>"best [X]" commercial cluster at Medium difficulty</b>, exactly what a curated directory answers.
        </Callout>
        <Callout eyebrow="Positioning call — Claude Code front door, broad catalog">
          Don't choose pure-narrow vs pure-broad — do both by role. Use <b style={{ color: T.ink }}>Claude Code as the hero brand</b> (highest volume, hottest social pull, topical authority) but architect the catalog to rank for the <b style={{ color: T.ink }}>Medium-KD "best [X] tools/servers"</b> queries across MCP, agentic, and vibe coding. Narrow front door, broad money pages.
        </Callout>
      </Page>
    </SectionBox>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 4 — THE MONEY
// ═══════════════════════════════════════════════════════════════════════════
function ScenarioCols() {
  const cols: { label: string; tone: string; rev: string; traffic: string; items: string[] }[] = [
    { label: "Conservative", tone: T.warn, rev: "$0–300/mo", traffic: "< 1–3k visits/mo", items: ["Base-rate outcome for most niche sites in yr 1", "A handful of paid submissions, no sponsors yet", "Traffic too thin for display ads"] },
    { label: "Base — SEO lands", tone: T.accent, rev: "$500–2.5k/mo", traffic: "10–30k visits/mo", items: ["Long-tail SEO + a real content engine working", "Display ads + 1–2 sponsors + paid listings", "Affiliate on AI-tool / course signups"] },
    { label: "Upside — breaks out", tone: T.good, rev: "$3k–6.5k/mo", traffic: "50–70k visits/mo", items: ["OpenAlternative-class outcome, 12–24 mo out", "Sponsorship inventory + newsletter + job board", "Requires the niche keyword demand to be real"] },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, background: T.line, border: `1px solid ${T.line}`, borderRadius: 16, overflow: "hidden" }}>
      {cols.map((c, i) => (
        <div key={i} style={{ background: c.label.startsWith("Base") ? T.accentWash : T.card, padding: "26px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontFamily: MONO, fontSize: 14, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, color: c.tone }}>{c.label}</div>
          <div style={{ fontFamily: MONO, fontSize: 30, fontWeight: 700, letterSpacing: "-0.02em", color: T.ink }}>{c.rev}</div>
          <div style={{ fontFamily: MONO, fontSize: 16, color: T.ink2 }}>{c.traffic}</div>
          <ul style={{ margin: "4px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 9 }}>
            {c.items.map((it, j) => (
              <li key={j} style={{ position: "relative", paddingLeft: 20, fontSize: 16, lineHeight: 1.45, color: T.ink2 }}>
                <span style={{ position: "absolute", left: 0, color: T.accent, fontFamily: MONO }}>→</span>{it}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
function MoneySection() {
  return (
    <SectionBox title="The Money" layout="custom">
      <Page w={1320}>
        <Eyebrow>03 · Monetization &amp; revenue</Eyebrow>
        <PageTitle>How these sites make money — and what year one realistically looks like.</PageTitle>
        <P>The levers are well-established; real solo-builder comparables prove the ceiling. The scenarios below are modeled from traffic × documented rate benchmarks — a shape, not a promise, since no verified revenue figure exists for this exact niche yet.</P>
        <StatGrid cards={[
          { lbl: "OpenAlternative", h: "~$80k/yr", body: "(~$6.5k/mo) at ~70k visits/mo. Solo, ~2–3 hrs/wk. ~65% ads + sponsorships, ~35% featured listings.", src: "dirstarter case study" },
          { lbl: "LibHunt", h: "~$10k+/mo", body: "(est.) at ~1.7M visits/mo — open-source projects directory, mostly display ads.", src: "indiehackers" },
          { lbl: "Rate card (Toolify)", h: "$50–100", body: "one-time skip-the-queue; **$100s–$1,000s/mo** per sponsored slot + newsletter mention.", src: "2026 submission guides" },
        ]} />
        <ScenarioCols />
        <div style={{ fontFamily: MONO, fontSize: 15, color: T.ink2, lineHeight: 1.6, background: T.soft, border: `1px solid ${T.line}`, borderRadius: 12, padding: "16px 18px" }}>
          Levers to stack: paid/express submission → display ads (at traffic) → sponsored/featured placements → affiliate → newsletter sponsorship → job board. Each unlocks at a higher traffic tier.
        </div>
      </Page>
    </SectionBox>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 5 — WHY WE WIN  (GEO thesis + 4 structural advantages)
// ═══════════════════════════════════════════════════════════════════════════
function WhyWeWinSection() {
  return (
    <SectionBox title="Why We Win" layout="custom">
      <Page w={1320} accent={T.accent}>
        <Eyebrow>SEO + AEO/GEO playbook · the strategic thesis</Eyebrow>
        <PageTitle>Be the source the AI quotes.</PageTitle>
        <Lede>In 2026 a directory can't just rank — it has to get cited by ChatGPT, Perplexity, and Google's AI Overviews, because those answers now eat most of the click. The tactics that win AI citations are a low-authority site's cheat code — and the ugly incumbents don't use them.</Lede>
        <div style={{ background: T.card, border: `1px solid ${T.line}`, borderLeft: `4px solid ${T.accent}`, borderRadius: 16, padding: "24px 26px" }}>
          <P>A peer-reviewed Princeton study proved that adding <b style={{ color: T.ink }}>cited statistics, expert quotes, and comparison data</b> lifts AI-answer visibility up to ~40% — and for a low-ranked page, citations alone lifted it <b style={{ color: T.ink }}>+115%</b>. The authors call it an "equalizer": it lets sites <i>without</i> authority surface anyway. That's exactly what a curated, fresh directory does natively — and what an auto-scraped markdown list never will.</P>
        </div>
        <KpiStrip items={[
          { v: "+115%", l: "AI-visibility lift from citations on a #5-ranked page (the equalizer)", accent: true },
          { v: "−38–58%", l: "Organic click-through lost to AI Overviews on triggered queries" },
          { v: "61.9%", l: "Of AI brand-citations come from 3rd-party editorial, not self-published" },
          { v: "$0", l: "Measurable value of llms.txt for citations — skip it" },
        ]} />
        <StatGrid cols={2} cards={[
          { lbl: "The equalizer", h: "GEO neutralizes authority.", body: "Citations, stats, and quotes lift AI visibility most for LOW-authority pages. A new domain, well-structured, can be quoted next to sites 100× its size. Free to us.", src: "Princeton GEO · arXiv 2311.09735" },
          { lbl: "The freshness gap", h: "Incumbents rot; we stay current.", body: "Awesome-lists go stale fast. AI engines and searchers reward freshness. Continuous re-verify + re-date is a durable edge the GitHub list can't match.", src: "answer-engine freshness signals" },
          { lbl: "The structure gap", h: "Machine-readable by design.", body: "Comparison tables, `schema.org`, clean entity data are what LLMs extract. Markdown gives them mush. `ItemList` + `SoftwareApplication` on every page.", src: "schema.org · Google Search Central" },
          { lbl: "The programmatic gap", h: "Hundreds of pages, correctly.", body: "One quality page per tool × category × comparison — the exact playbook that took OpenAlternative to $80k/yr. Volume without thin content is our home-field.", src: "OpenAlternative case study" },
        ]} />
      </Page>
    </SectionBox>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 6 — THE PLAYBOOK  (two engines + technical + launch)
// ═══════════════════════════════════════════════════════════════════════════
function PlaybookSection() {
  return (
    <SectionBox title="The Playbook" layout="custom">
      <Page w={1320}>
        <Eyebrow>The two-front war · blue links + AI citations</Eyebrow>
        <PageTitle>Two engines: the page factory and the citation machine.</PageTitle>

        <Eyebrow color={T.ink2}>Engine 1 · Programmatic SEO — one quality page per intent, at scale</Eyebrow>
        <Table
          headers={["Page type", "Targets the query", "Est. pages", "Why it ranks"]}
          hiFirst
          rows={[
            [{ text: "Category / \"best\" hub", sub: "/best-mcp-servers" }, { text: "best [X] tools / servers", mono: true }, { text: "30–60", mono: true }, { text: "Medium-KD commercial intent — the money pages" }],
            [{ text: "Individual tool page", sub: "/tool/[name]" }, { text: "[tool] review / features", mono: true }, { text: "300–1000+", mono: true }, { text: "Long-tail brand + \"is [tool] good\" queries" }],
            [{ text: "Comparison page", sub: "/compare/[a]-vs-[b]" }, { text: "[a] vs [b]", mono: true }, { text: "100–400", mono: true }, { text: "High-intent, low-competition, AI loves tables" }],
            [{ text: "Alternative page", sub: "/alternatives/[tool]" }, { text: "[tool] alternatives", mono: true }, { text: "100–300", mono: true }, { text: "Proven directory traffic driver" }],
            [{ text: "Language / use-case", sub: "/python · /for-beginners" }, { text: "[X] for [language/use]", mono: true }, { text: "50–150", mono: true }, { text: "Captures the beginner-onboarding wedge" }],
          ]}
        />
        <Callout eyebrow="The one hard rule — don't ship thin" tone="warn">
          <b style={{ color: T.ink }}>"Scales automatically" was refuted 0–3 in verification.</b> Each page must earn its rank with genuinely unique value — a real summary, distinct comparison, verified metadata, a screenshot. Hundreds of near-duplicate stubs trigger thin-content penalties and sink the whole domain. Generate at scale, but every page clears a unique-content bar before it's indexed.
        </Callout>

        <Eyebrow color={T.ink2}>Engine 2 · AEO / GEO — get quoted by the machines</Eyebrow>
        <Table
          headers={["Tactic", "Measured effect", "Do this on every page"]}
          hiFirst
          rows={[
            [{ text: "Cite sources" }, { text: "+115% (#5-ranked)", mono: true, strong: T.good }, { text: "Link real stats/studies inline. The equalizer — biggest lever for a new domain." }],
            [{ text: "Add expert quotes" }, { text: "~+28%", mono: true, strong: T.good }, { text: "Pull a real maintainer/dev quote into each tool + comparison page." }],
            [{ text: "Add statistics" }, { text: "up to ~+40%", mono: true, strong: T.good }, { text: "GitHub stars, release cadence, benchmarks — quantify every entry." }],
            [{ text: "Comparison tables" }, { text: "high extractability", mono: true, strong: T.good }, { text: "Structured tables are what LLMs lift verbatim into answers." }],
            [{ text: "Keyword stuffing", }, { text: "−10%", mono: true, strong: T.crit }, { text: "Don't. Actively hurts AI visibility. Write for humans." }],
            [{ text: "llms.txt file" }, { text: "~0 (null)", mono: true }, { text: "No measurable effect across 300k domains. Skip / deprioritize." }],
          ]}
        />
        <Callout eyebrow="The off-page multiplier — earned media beats self-praise" tone="solid">
          <b style={{ color: T.ink }}>61.9% of AI brand-citations come from third-party editorial, reviews, and awards</b> — not what you publish about yourself (Muck Rack puts earned media at ~84%). Getting the directory <i>mentioned, reviewed, and included in other people's lists</i> is the highest-leverage citation work.
        </Callout>

        <Eyebrow color={T.ink2}>Technical foundation — non-negotiable hygiene</Eyebrow>
        <Bullets items={[
          "**Schema.org on every page** — `ItemList` on listings, `SoftwareApplication` per tool, `FAQPage` on hubs, `BreadcrumbList` everywhere.",
          "**Control faceted-navigation index bloat** — `robots.txt` disallow facet URLs → fragment filters → `rel=canonical` → `noindex,follow`. Decide before launch.",
          "**Sitemaps + canonicalization at scale** — segmented XML sitemaps, one canonical per intent, fast Core Web Vitals. Cloudflare-static is cheap + fast.",
          "**Instrument AI-referral tracking day one** — GA4 custom channel regex for `chatgpt.com`, `perplexity.ai`, `claude.ai`, `gemini`, `copilot`. You can't optimize AI traffic you can't see.",
        ]} />

        <Eyebrow color={T.ink2}>Launch &amp; link-building — the motion that earns the citations</Eyebrow>
        <StatGrid cards={[
          { lbl: "Launch spikes", h: "PH + HN + Reddit", body: "OpenAlternative hit ~100k uniques in launch week. One coordinated day; a Show HN; r/ClaudeAI, r/programming.", },
          { lbl: "Be in the lists", h: "Get into the awesome-lists", body: "Submit the directory to `awesome-claude-code`, awesome-mcp. Turn the incumbent's authority into YOUR backlink. Judo, not war.", },
          { lbl: "Earned coverage", h: "Newsletters & digital PR", body: "Pitch TLDR, Bytes; write \"state of Claude Code tooling\" data posts. The 62% lever AI engines cite.", },
        ]} />
      </Page>
    </SectionBox>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 7 — THE ROADMAP
// ═══════════════════════════════════════════════════════════════════════════
function RoadmapSection() {
  const phases: { when: string; title: string; mid?: boolean; items: string[] }[] = [
    { when: "0–3 mo · Foundation", title: "Build the machine, ship it right.", items: ["Lock positioning (Claude Code front door, broad catalog)", "Build the programmatic engine: category + tool + comparison templates", "Ship 100–300 pages, each past the unique-content bar", "GEO-load every page: cited stats, quotes, tables", "Schema + faceted control + sitemaps + GA4 AI tracking", "Weekend launch: PH / HN / Reddit + get into awesome-lists"] },
    { when: "3–6 mo · Authority", title: "Earn the citations, expand the catalog.", mid: true, items: ["Digital-PR push: newsletters, \"state of tooling\" data posts", "Build out comparison + alternatives money pages", "Monitor AI citations + rankings; double down on what surfaces", "Turn on monetization rails (paid submit + sponsor slots)", "Kill or rewrite pages that aren't ranking (prune thin)"] },
    { when: "6–12 mo · Scale", title: "Compound what works.", items: ["Scale to thousands of pages — quality-gated only", "Layer revenue: display ads + sponsorships + newsletter", "Deepen the freshness engine (auto re-verify + re-date)", "Target the OpenAlternative benchmark: ~70k visits/mo", "Distill the whole playbook into a reusable SEO skill"] },
  ];
  return (
    <SectionBox title="The Roadmap" layout="custom">
      <Page w={1320}>
        <Eyebrow>Phased 0–12 months — sequenced by leverage</Eyebrow>
        <PageTitle>From foundation to compounding traffic.</PageTitle>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, background: T.line, border: `1px solid ${T.line}`, borderRadius: 16, overflow: "hidden" }}>
          {phases.map((p, i) => (
            <div key={i} style={{ background: p.mid ? T.accentWash : T.card, padding: "26px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontFamily: MONO, fontSize: 14, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, color: T.accent }}>{p.when}</div>
              <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em", color: T.ink }}>{p.title}</div>
              <ul style={{ margin: "2px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 9 }}>
                {p.items.map((it, j) => (
                  <li key={j} style={{ position: "relative", paddingLeft: 20, fontSize: 16, lineHeight: 1.45, color: T.ink2 }}>
                    <span style={{ position: "absolute", left: 0, color: T.accent, fontFamily: MONO }}>→</span>{it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Page>
    </SectionBox>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 8 — THE BUSINESS PLAN  (link to the pitch deck + video)
// ═══════════════════════════════════════════════════════════════════════════
function BusinessPlanSection() {
  return (
    <SectionBox title="The Business Plan" layout="custom">
      <Page w={1320} accent={T.accent}>
        <Eyebrow>The deck · a full business plan</Eyebrow>
        <PageTitle>The venture as an investor-grade pitch deck.</PageTitle>
        <Lede>Market sizing, unit economics, a 0–12–24-month financial model, KPIs, milestones, risks, and the funding framing — authored as a narrated slide deck, on this dashboard.</Lede>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
          <a href="/pitch-doc/open-source/business-plan" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 12, background: T.accent, color: "#FFFFFF", fontSize: 22, fontWeight: 700, borderRadius: 14, padding: "20px 34px" }}>
            ▶ Open the business-plan deck
          </a>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12, background: T.soft, border: `1px solid ${T.line}`, color: T.ink2, fontSize: 20, fontWeight: 600, borderRadius: 14, padding: "20px 30px" }}>
            🎬 Narrated video walkthrough — in the deck
          </div>
        </div>
        <Callout eyebrow="What's inside the deck">
          Executive summary · the problem &amp; solution · market sizing (TAM/SAM/SOM, keyword-demand bottom-up) · competition · GTM &amp; SEO strategy · revenue model &amp; unit economics · a 3-scenario financial projection · KPIs &amp; milestones · risks &amp; mitigations · the funding framing. Every slide is voiced.
        </Callout>
      </Page>
    </SectionBox>
  );
}

// ── the one grid node holding every SectionBox ───────────────────────────────
function GridNode() {
  return (
    <SectionGrid width={3760} gap={200}>
      <OpportunitySection />
      <CompetitionSection />
      <DemandSection />
      <MoneySection />
      <WhyWeWinSection />
      <PlaybookSection />
      <RoadmapSection />
      <BusinessPlanSection />
    </SectionGrid>
  );
}

const nodeTypes: NodeTypes = {
  grid: GridNode as unknown as NodeTypes[string],
};

function buildNodes(): Node[] {
  return [{ id: "grid", type: "grid", position: { x: 0, y: 0 }, draggable: false, data: {} }];
}

export function OpenSourceBoardPage() {
  const nodes = useMemo(() => buildNodes(), []);
  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <BoardCanvas nodes={nodes} nodeTypes={nodeTypes} fitViewPadding={0.14} />
    </div>
  );
}
